import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import {
  hiringLinksAPI,
  type HiringCancelConfirm,
  type HiringCancelPreview,
  type HiringCancelResult,
  type HiringLink,
} from '../../services/api';
import {
  CANCEL_REASON_MAX,
  CANCEL_WORDS,
  HIRING_CODES,
  cancelErrorText,
  cancelReasonOk,
  cancelRefusalStep,
  hiringCodeOf,
  hiringErrorText,
  hiringStatusOf,
  isHiringOff,
  makeCancelRequestKey,
} from './hiringText';

/**
 * The foot of one opening's card, for a person who may cancel it: "Stop following this
 * opening" beside "Cancel hiring in Recruitment", and everything the second one leads to.
 *
 * THE ONE PLACE ON THE WEB THAT CAN MAKE RECRUITMENT DELETE AN OPENING. Recruitment
 * deletes it at once, in the name of the person who confirms, and then tells every
 * candidate still in process that the position is closed. It cannot be undone. So:
 *
 *   - The first click only asks the server for a review (`previewCancel`). Nothing is
 *     sent to Recruitment and nothing is saved.
 *   - The review is the server's own: its title, its sentences, its two buttons and the
 *     line under them. This component adds the reason box and nothing else.
 *   - "Yes, cancel this opening" is offered only once a reason of 3 characters is typed,
 *     and focus lands on "No, keep the opening".
 *   - The key of the confirm is made ONCE, when the review arrives, and every repeat of
 *     that confirm carries it: the server answers a repeat from the first request, so one
 *     review can never delete twice.
 *   - When no answer comes back, the only thing to press is "Ask again", which repeats
 *     the same key, the same hash and the same reason. The review is not offered again:
 *     the cancel may have gone out.
 *   - There is no "ask someone to cancel" path. A person the checks refuse reads the
 *     server's sentence, and "Close".
 *
 * A link somebody else stopped or cancelled first is the card's to show as well
 * (`onLinkGone`), exactly as it shows one that "Stop following" found already stopped.
 *
 * What a cancel came to (`onResult`) is the card's to show: this component only tells it.
 * It is mounted only for a link that is followed, is not shared, has an opening number
 * and has no cancel open - the card decides that.
 */

type Step = 'idle' | 'reading' | 'review' | 'refused' | 'sending' | 'no_answer';

const SMALL_BUTTON = 'rounded-full border px-3 py-1 text-xs font-semibold transition-colors disabled:opacity-50';

export function CancelHiringStep({
  projectId, link, stopButton, besideCancel, startReview = false, onResult, onCancelGone, onHiringGone, onListStale, onLinkGone,
}: {
  projectId: number;
  link: HiringLink;
  /**
   * "Cancel opening" was already chosen outside this window (the question asked when someone is
   * deployed, DeployHiringCheck): the review is asked for once, when the step is first shown,
   * exactly as the button asks for it. Everything after it is the same.
   */
  startReview?: boolean;
  /** "Stop following this opening", exactly as the card draws it without Cancel; `disabled` while Recruitment is being read. */
  stopButton: (disabled: boolean) => ReactNode;
  /** Drawn between "Cancel opening" and "Stop following" while nothing of a cancel is asked ("Move to another store"); `disabled` as for the stop. */
  besideCancel?: (disabled: boolean) => ReactNode;
  /** A cancel was answered once a claim existed: how it ended, or that it is not confirmed. */
  onResult: (result: HiringCancelResult) => void;
  /** Cancel was switched off for the project, or stopped on the server, since the page asked: the control goes. */
  onCancelGone: () => void;
  /** Hiring itself was switched off for the project. */
  onHiringGone: () => void;
  /**
   * The page's list is out of date: read it again. 'refused': a review or a confirm was
   * refused because of it (a cancel of this opening is open, or Recruitment no longer lists
   * it). 'lost': no answer came back from a confirm, so the cancel may have gone out - and
   * may be why the list no longer holds the link. Either way the card must stay on show.
   */
  onListStale: (why: 'refused' | 'lost') => void;
  /** The link is no longer followed (someone stopped or cancelled it first). The card says so, in `sentence`, and keeps itself on show. */
  onLinkGone: (sentence: string) => void;
}) {
  const [step, setStep] = useState<Step>('idle');
  const [preview, setPreview] = useState<HiringCancelPreview | null>(null);
  const [reason, setReason] = useState('');
  // Said above a review that replaced one the server found out of date, or beside "Ask again".
  const [notice, setNotice] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  const reasonId = useId();
  const helpId = useId();

  // The key of the confirm on show: made when its review arrived, and never for another.
  const requestKey = useRef<string | null>(null);
  // An answer of this confirm was lost: from then on only the same request may be repeated.
  const lost = useRef(false);
  // One call at a time, whatever asks for it.
  const working = useRef(false);
  const alive = useRef(true);
  // The control was just put back: it takes keyboard focus, as the button that was pressed has left the page.
  const refocus = useRef(false);
  const owner = useRef({ onResult, onCancelGone, onHiringGone, onListStale, onLinkGone });
  owner.current = { onResult, onCancelGone, onHiringGone, onListStale, onLinkGone };

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const backToIdle = () => {
    refocus.current = true;
    requestKey.current = null;
    lost.current = false;
    setPreview(null);
    setNotice(null);
    setRefusal(null);
    setStep('idle');
  };

  /** Cancel, or hiring itself, is off: the owner takes the control away. True when that was it. */
  const switchedOff = (err: unknown): boolean => {
    if (isHiringOff(err)) {
      owner.current.onHiringGone();
      return true;
    }
    if (cancelRefusalStep(err) === 'control_goes') {
      owner.current.onCancelGone();
      return true;
    }
    // Someone stopped following it, or cancelled it, first: the card is the one to say so.
    const code = hiringCodeOf(err);
    if (code === HIRING_CODES.linkNotLive || code === HIRING_CODES.linkNotFound) {
      owner.current.onLinkGone(hiringErrorText(err, CANCEL_WORDS.reviewFailed));
      return true;
    }
    return false;
  };

  /** The review: every check, and what Recruitment will do. Sends nothing, saves nothing. */
  const review = async (said: string | null = null) => {
    if (working.current) return;
    working.current = true;
    setStep('reading');
    setRefusal(null);
    setNotice(said);
    try {
      const res = await hiringLinksAPI.previewCancel(projectId, link.id);
      const answered = res?.data?.data;
      if (!alive.current) return;
      if (!answered || typeof answered.previewHash !== 'string' || !Array.isArray(answered.sentences)) {
        setRefusal(CANCEL_WORDS.reviewFailed);
        setStep('refused');
        return;
      }
      // One key per review, made as it arrives: a second review is a second confirm.
      requestKey.current = makeCancelRequestKey();
      lost.current = false;
      setPreview(answered);
      setStep('review');
    } catch (err) {
      if (switchedOff(err)) return;
      if (cancelRefusalStep(err) === 'list_reread') owner.current.onListStale('refused');
      if (!alive.current) return;
      setRefusal(cancelErrorText(err, CANCEL_WORDS.reviewFailed));
      setStep('refused');
    } finally {
      working.current = false;
    }
  };

  // Asked for once, when the step is first shown, never again on a later render.
  const startedReview = useRef(false);
  useEffect(() => {
    if (!startReview || startedReview.current) return;
    startedReview.current = true;
    void review();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** "Yes, cancel this opening", and every "Ask again" after it: the same key, hash and reason. */
  const confirm = async () => {
    const key = requestKey.current;
    if (!preview || !key || working.current || !cancelReasonOk(reason, preview.reason)) return;
    working.current = true;
    setStep('sending');
    setRefusal(null);
    const body: HiringCancelConfirm = { reason: reason.trim(), requestKey: key, previewHash: preview.previewHash };
    try {
      const res = await hiringLinksAPI.cancel(projectId, link.id, body);
      const answered = res?.data?.data;
      if (!answered || !answered.link || typeof answered.outcome !== 'string') {
        // An answer that cannot be read is no answer: only the same request may be repeated.
        lost.current = true;
        owner.current.onListStale('lost');
        if (alive.current) {
          setNotice(null);
          setStep('no_answer');
        }
        return;
      }
      // The owner is told whether or not this card is still on show.
      owner.current.onResult(answered);
      if (alive.current) {
        requestKey.current = null;
        lost.current = false;
        setPreview(null);
        setReason('');
        setNotice(null);
        setStep('idle');
      }
    } catch (err) {
      if (switchedOff(err)) return;
      const status = hiringStatusOf(err);
      const refused = status !== undefined && status < 500 && !!hiringCodeOf(err);
      if (!refused) {
        // No answer, or an error of the server's own: the cancel may have gone out.
        lost.current = true;
        owner.current.onListStale('lost');
        if (!alive.current) return;
        setNotice(hiringCodeOf(err) ? cancelErrorText(err, '') || null : null);
        setStep('no_answer');
        return;
      }
      // A definite "no" from the checks: nothing was sent by this request.
      const next = cancelRefusalStep(err);
      const sentence = cancelErrorText(err, CANCEL_WORDS.reviewFailed);
      if (next === 'list_reread' || lost.current) owner.current.onListStale('refused');
      if (!alive.current) return;
      if (next === 'review_again' && !lost.current) {
        // What was reviewed is no longer so. A fresh review, with a new key; the reason typed is kept.
        working.current = false;
        await review(CANCEL_WORDS.reviewedAgain);
        return;
      }
      setRefusal(sentence);
      setStep('refused');
    } finally {
      working.current = false;
    }
  };

  if (step === 'idle' || step === 'reading') {
    const reading = step === 'reading';
    // The approved foot (15 Oct 2026, panel 2): Cancel opening first, quiet, then Stop following.
    // What each does is on its hover, not in lines under them.
    return (
      <div className="space-y-1">
        <div className="flex flex-wrap items-center gap-1">
          <button
            type="button"
            ref={(el) => {
              if (el && refocus.current) {
                el.focus();
                if (document.activeElement === el) refocus.current = false;
              }
            }}
            onClick={() => void review()}
            disabled={reading}
            title={CANCEL_WORDS.cancelExplained}
            className="inline-flex h-9 items-center gap-1 rounded-lg px-2 text-sm font-medium text-red-700 transition-colors hover:bg-red-50 disabled:opacity-50"
          >
            {reading && <Loader2 className="h-3 w-3 animate-spin" />}
            {CANCEL_WORDS.button}
          </button>
          {besideCancel?.(reading)}
          <span title={CANCEL_WORDS.stopExplained}>{stopButton(reading)}</span>
        </div>
        {reading && <p role="status" className="text-xs text-gray-600">{CANCEL_WORDS.reading}</p>}
      </div>
    );
  }

  if (step === 'refused') {
    return (
      <div role="group" aria-label={CANCEL_WORDS.confirmGroupLabel(link.openingId)} className="space-y-2 border-t border-gray-100 pt-3">
        <p role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{refusal}</p>
        <button
          type="button"
          autoFocus
          onClick={backToIdle}
          className={`${SMALL_BUTTON} border-gray-300 text-gray-700 hover:bg-gray-50`}
        >
          {CANCEL_WORDS.close}
        </button>
      </div>
    );
  }

  if (step === 'sending' || step === 'no_answer') {
    const sending = step === 'sending';
    return (
      <div role="group" aria-label={CANCEL_WORDS.confirmGroupLabel(link.openingId)} className="space-y-2 border-t border-gray-100 pt-3">
        {sending ? (
          <p role="status" className="inline-flex items-center gap-2 text-sm text-gray-800">
            <Loader2 className="h-4 w-4 animate-spin" />
            {CANCEL_WORDS.sending}
          </p>
        ) : (
          <>
            {/* The server's own sentence when it answered with an error of its own; else that nothing came back. */}
            <p role="alert" className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">{notice || CANCEL_WORDS.noAnswer}</p>
            <button
              type="button"
              autoFocus
              onClick={() => void confirm()}
              className={`${SMALL_BUTTON} border-gray-300 text-gray-700 hover:bg-gray-50`}
            >
              {CANCEL_WORDS.askAgain}
            </button>
          </>
        )}
      </div>
    );
  }

  // The review. Its words are the server's; the reason box is this screen's.
  const ready = !!preview && cancelReasonOk(reason, preview.reason);
  return (
    <div
      role="group"
      aria-label={CANCEL_WORDS.confirmGroupLabel(preview?.link.openingId ?? link.openingId)}
      className="space-y-3 rounded-lg border border-red-200 bg-red-50/40 p-3"
    >
      {notice && <p role="status" className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">{notice}</p>}
      <p className="text-sm font-semibold text-gray-900">{preview?.title}</p>
      <ul className="list-disc space-y-1 pl-5 text-sm text-gray-800">
        {(preview?.sentences ?? []).map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>
      <div className="space-y-1">
        <label htmlFor={reasonId} className="block text-xs font-semibold text-gray-700">{preview?.reason?.label || CANCEL_WORDS.reasonLabel}</label>
        <textarea
          id={reasonId}
          aria-describedby={helpId}
          rows={2}
          maxLength={typeof preview?.reason?.max === 'number' ? preview.reason.max : CANCEL_REASON_MAX}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
        />
        <p id={helpId} className="text-xs text-gray-500">{CANCEL_WORDS.reasonHelp}</p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {/* Focus lands on the safe answer: the button that was pressed has just left the page. */}
        <button
          type="button"
          autoFocus
          onClick={backToIdle}
          className={`${SMALL_BUTTON} border-gray-300 bg-white text-gray-700 hover:bg-gray-50`}
        >
          {preview?.confirm?.no || CANCEL_WORDS.no}
        </button>
        <button
          type="button"
          onClick={() => void confirm()}
          disabled={!ready}
          className={`${SMALL_BUTTON} border-red-600 bg-red-600 text-white hover:bg-red-700`}
        >
          {preview?.confirm?.yes || CANCEL_WORDS.yes}
        </button>
      </div>
      <p className="text-xs text-gray-500">{preview?.confirm?.footnote || CANCEL_WORDS.footnote}</p>
    </div>
  );
}

export default CancelHiringStep;
